import React from 'react';
import {useTransition} from 'react-spring';

import throttle from 'lodash/throttle';

import {events} from '@edna/utils';

import {useTheme} from '../styles';
import {EEvent} from '../types';
import {Item} from './Item';
import * as S from './style';
import {
  DEFAULT_NOTIFICATION_OFFSET,
  DEFAULT_VISIBLE_COUNT,
  TNewNotification,
  TNewNotificationOptions,
} from './types';
import {checkForDuplicates, createNotification} from './utils';

type TProps = {
  className?: string;
  visibleCount?: number;
  isDuplicate?: TAnyFunction;
  isCheckDuplicates?: boolean;
};

type TContainerProps = {
  onClose: (notificationId: TNewNotification['id']) => void;
  notifications: TNewNotification[];
  onAnimate?: (height?: number) => void;
};

const AnimatedContainer = React.memo<TContainerProps>(({notifications, onClose, onAnimate}) => {
  const refMap = React.useMemo(() => new WeakMap(), []);
  const {
    animation: {duration},
  } = useTheme();

  const startPosition = {
    y: 0,
    height: 0,
    overflow: 'hidden',
  };

  const transitions = useTransition(notifications, {
    from: startPosition,
    enter: (item) => async (next) => {
      onAnimate?.(refMap.get(item)?.offsetHeight ?? 0);
      await next({
        overflow: 'visible',
        height: refMap.get(item)?.offsetHeight,
        y: DEFAULT_NOTIFICATION_OFFSET,
      });
    },
    leave: (item) => async (next) => {
      onAnimate?.(-refMap.get(item)?.offsetHeight ?? 0);
      await next(startPosition);
    },
    config: {duration},
  });

  return transitions((style, item) => (
    <Item
      {...item}
      key={item.id}
      removeNotification={onClose}
      style={style}
      innerRef={(ref) => ref && refMap.set(item, ref)}
    />
  ));
});

const NewNotifications = React.memo<TProps>(
  ({
    className,
    visibleCount = DEFAULT_VISIBLE_COUNT,
    isDuplicate = checkForDuplicates,
    isCheckDuplicates,
  }) => {
    const [list, setList] = React.useState<TNewNotification[]>([]);
    const listRef = React.useRef<TNewNotification[]>([]);
    const queueRef = React.useRef<TNewNotification[]>([]);
    const [topOffset, setTopOffset] = React.useState<number>(DEFAULT_NOTIFICATION_OFFSET);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (visibleCount && listRef.current.length < visibleCount && queueRef.current.length) {
        const first = queueRef.current.shift();

        if (first) {
          queueRef.current = [...queueRef.current];
          listRef.current = [...listRef.current, first];
          setList([...listRef.current]);
        }
      }
    }, [listRef.current.length, visibleCount]);

    const permanentNotifications = list.filter((item) => item.isPermanent);
    const baseNotifications = list.filter((item) => !item.isPermanent);

    const calculateTopOffset = React.useCallback((targetHeight: number = 0) => {
      if (!containerRef?.current) {
        return setTopOffset(DEFAULT_NOTIFICATION_OFFSET);
      }

      const {top, height} = containerRef.current.getBoundingClientRect();
      const targetNotificationsHeight = targetHeight + height;

      if (!targetNotificationsHeight) {
        return setTopOffset(DEFAULT_NOTIFICATION_OFFSET);
      }

      const newTopOffset = Math.max(top + targetNotificationsHeight, DEFAULT_NOTIFICATION_OFFSET);

      setTopOffset(newTopOffset);
    }, []);

    const addNotification = React.useCallback(
      (notification: TNewNotificationOptions) => {
        if (
          isCheckDuplicates &&
          isDuplicate([...queueRef.current, ...listRef.current], notification)
        ) {
          return;
        }

        const newNotification = createNotification(notification);

        if (visibleCount && listRef.current.length < visibleCount) {
          listRef.current = [...listRef.current, newNotification];
          setList(listRef.current);
        } else {
          queueRef.current = [...queueRef.current, newNotification];
        }
      },
      [visibleCount, isCheckDuplicates, isDuplicate],
    );

    const removeNotification = React.useCallback((notificationId: TNewNotification['id']) => {
      listRef.current = listRef.current.filter(({id}) => id !== notificationId);
      setList(listRef.current);
    }, []);

    React.useEffect(() => {
      events.on(EEvent.ADD_NEW_NOTIFICATION, addNotification);
      events.on(EEvent.REMOVE_NEW_NOTIFICATION, removeNotification);

      return () => {
        events.off(EEvent.ADD_NEW_NOTIFICATION, addNotification);
        events.off(EEvent.REMOVE_NEW_NOTIFICATION, removeNotification);
      };
    }, []);

    const optimizedCalculateTopOffset = React.useMemo(
      () => throttle(() => calculateTopOffset(), 50),
      [],
    );

    React.useEffect(() => {
      if (permanentNotifications.length) {
        window.addEventListener('scroll', optimizedCalculateTopOffset);
      }

      return () => window.removeEventListener('scroll', optimizedCalculateTopOffset);
    }, [permanentNotifications.length]);

    return (
      <>
        <S.PermanentNotifications className={className} ref={containerRef}>
          <AnimatedContainer
            notifications={permanentNotifications}
            onClose={removeNotification}
            onAnimate={calculateTopOffset}
          />
        </S.PermanentNotifications>
        <S.Notifications className={className} topOffset={topOffset}>
          <AnimatedContainer notifications={baseNotifications} onClose={removeNotification} />
        </S.Notifications>
      </>
    );
  },
);

export {NewNotifications};
